import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

// spec doji-bg.md AC-BDB-013: 初期表示順序は北海道〜沖縄の 10 エリア (北→南)。
// 沖縄は JEPX 取引対象外だが (`~/models/area` JEPX_AREA_CODES の godoc 参照)、
// 需要ダッシュボードは JEPX 限定ではなく AREA_CODE_OPTIONS と同じ全 10 エリアが
// 対象 (以前は 9 エリアで沖縄が抜けていた欠落。#7064)。
export const DEMAND_AREA_CODES = [
  AreaCode.HOKKAIDO,
  AreaCode.TOHOKU,
  AreaCode.TOKYO,
  AreaCode.CHUBU,
  AreaCode.HOKURIKU,
  AreaCode.KANSAI,
  AreaCode.CHUGOKU,
  AreaCode.SHIKOKU,
  AreaCode.KYUSHU,
  AreaCode.OKINAWA,
] as const

export { GC_KOMA } from '~/constants/koma'

export type SeriesKey =
  | 'demandPlan'
  | 'demandActual'
  | 'weather'
  | 'temperature'
  | 'precipitation'
  | 'procurementNet'
  | 'demandActualCorrected'
  | 'voltageExtraHigh'
  | 'voltageHigh'
  | 'voltageLow'
  | 'dewPoint'

export type SeriesVisibility = Record<SeriesKey, boolean>

/**
 * 電圧階級別グループの子系列 (doji-bg.md AC-BDB-031「電圧階級別」配下)。
 * 積み上げの下段から順に並べる (低圧 → 高圧 → 特別高圧)。
 */
export const VOLTAGE_SERIES_KEYS = [
  'voltageLow',
  'voltageHigh',
  'voltageExtraHigh',
] as const satisfies readonly SeriesKey[]

/** 分析補助グループの子系列 (doji-bg.md AC-BDB-031「分析補助」配下)。 */
export const ANALYSIS_SERIES_KEYS = [
  'temperature',
  'precipitation',
  'procurementNet',
] as const satisfies readonly SeriesKey[]

/** 詳細グループの子系列 (doji-bg.md AC-BDB-031「詳細」配下)。 */
export const DETAIL_SERIES_KEYS = [
  'demandActualCorrected',
  'dewPoint',
] as const satisfies readonly SeriesKey[]

/**
 * 一覧 (ダッシュボード) の系列トグル初期値。
 *
 * spec (AC-BDB-003)・design ともに親・子とも全系列を初期 ON。
 *
 * 以前は design `balance/bg.html` が「降水量・調達量 off / 詳細 親 off」だったため
 * 双方が ON と言う気温だけを ON にして残りを判断待ちにしていたが、 2026-08-12 の
 * PdM 判断 (「ダッシュボードも詳細 ON」) と spec PR #5276 で決着し、 design 側も
 * `bg.html` の該当 switch-item / `chk-analysis` / `chk-detail` がすべて on・checked
 * に更新済み。 これに合わせて全系列を初期 ON にする (issue #5088)。
 *
 * なお `temperature` トグルの正名は「気温/予想気温」で 2 系列を駆動する契約だが、
 * 一覧は実績のみ描く (予想気温の破線は未実装 — issue #5183 で確認依頼)。
 */
export const DEFAULT_SERIES_VISIBILITY: SeriesVisibility = {
  demandPlan: true,
  demandActual: true,
  weather: true,
  temperature: true,
  precipitation: true,
  procurementNet: true,
  demandActualCorrected: true,
  // 電圧階級別は AC-BDB-003 / AC-BDB-016 で既定 ON (issue #4734)。
  voltageExtraHigh: true,
  voltageHigh: true,
  voltageLow: true,
  dewPoint: true,
}
